import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ToolPage, { inputCls, labelCls, primaryBtn } from "@/components/ToolPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionLabel } from "@/components/Eyebrow";
import { Download } from "lucide-react";

const SCHOOLS = ["UF", "UCF", "FSU", "USF", "FIU", "Harvard"];

function icsFor(d) {
  const dt = (d.date || "").replace(/-/g, "");
  const uid = `${d.deadline_id}@beyondvalencia`;
  const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//BeyondValencia//Tools//EN",
    "BEGIN:VEVENT", `UID:${uid}`, `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${dt}`,
    `SUMMARY:${d.school} — ${d.type} (${d.cycle_label})`,
    `DESCRIPTION:${(d.notes || "").replace(/\n/g, " ")}${d.strategy_commentary ? "\\n\\n" + d.strategy_commentary : ""}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
}

function downloadIcs(d) {
  const blob = new Blob([icsFor(d)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${d.school}_${d.type}_${d.date}.ics`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

export default function Deadlines() {
  const { user } = useAuth();
  const [all, setAll] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [intendedTerm, setIntendedTerm] = useState("Fall 2026");
  const [targets, setTargets] = useState({ UF: true, UCF: true, FSU: false, USF: false, FIU: false, Harvard: false });
  const [email, setEmail] = useState("");
  const [view, setView] = useState("list");

  useEffect(() => { (async () => {
    try { const res = await api.get("/tools/deadlines"); setAll(res.data || []); setFiltered(res.data || []); }
    catch {} finally { setLoading(false); }
  })(); }, []);

  const personalize = async (e) => {
    e?.preventDefault?.();
    const target_schools = Object.entries(targets).filter(([, v]) => v).map(([k]) => k);
    if (target_schools.length === 0) { toast.error("Select at least one school."); return; }
    if (!user && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { toast.error("Please enter a valid email."); return; }
    setRunning(true);
    try {
      const res = await api.post("/tools/deadlines/personalized", { target_schools, intended_term: intendedTerm, email: email || undefined });
      setFiltered(res.data.deadlines || []);
      toast.success("Personalized.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Personalization failed.");
    } finally { setRunning(false); }
  };

  return (
    <ToolPage
      numeral="V"
      name="Deadline Tracker"
      blurb="Curated deadlines for UF, UCF, FSU, USF, FIU, and Harvard transfer cycles. Personalize for strategy commentary."
      seoTitle="Florida Transfer Deadlines — UF, UCF, FSU, USF, FIU, Harvard | Beyond Valencia"
      seoDescription="Curated transfer deadlines for UF, UCF DirectConnect, FSU, USF, FIU, and Harvard. Personalize by term and download to your calendar."
      seoCanonical="/tools/deadlines"
      seoKeywords="UF transfer deadline, UCF transfer deadline, FSU transfer deadline, Harvard transfer deadline, Florida transfer dates"
    >
      <div className="border border-stone/40 bg-bone-dim p-6 mb-10" data-testid="deadlines-personalize">
        <SectionLabel index="A" label="Personalize" />
        <form onSubmit={personalize} className="mt-5 space-y-4">
          <div className="space-y-2"><Label className={labelCls}>Intended term</Label><Input className={inputCls + " max-w-xs"} value={intendedTerm} onChange={(e) => setIntendedTerm(e.target.value)} /></div>
          <div className="space-y-2"><Label className={labelCls}>Target schools</Label>
            <div className="flex flex-wrap gap-3">
              {SCHOOLS.map((s) => (
                <button type="button" key={s} onClick={() => setTargets({ ...targets, [s]: !targets[s] })} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${targets[s] ? "bg-ivy text-bone" : "border border-stone/40 text-ink-soft hover:border-ink"}`} data-testid={`deadline-school-${s}`}>{s}</button>
              ))}
            </div>
          </div>
          {!user && (<div className="space-y-2"><Label className={labelCls}>Email (required for personalization)</Label><Input type="email" data-testid="deadlines-email" className={inputCls + " max-w-md"} value={email} onChange={(e) => setEmail(e.target.value)} /></div>)}
          <button type="submit" disabled={running} className={primaryBtn} data-testid="deadlines-personalize-btn">{running ? "Personalizing…" : "Apply"}</button>
        </form>
      </div>

      <SectionLabel index="B" label="Deadlines" />
      <div className="mt-4 flex border border-stone/40 w-fit" data-testid="deadlines-view-toggle">
        {[["list", "List"], ["calendar", "Calendar"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 ${view === v ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`} data-testid={`deadlines-view-${v}`}>{l}</button>
        ))}
      </div>
      {loading ? <div className="mt-6 text-ink-soft">Loading…</div> : view === "list" ? (
        <div className="mt-6 border-t border-l border-stone/40" data-testid="deadlines-table">
          {filtered.length === 0 && <div className="border-r border-b border-stone/40 bg-bone p-6 text-ink-soft text-sm">No deadlines match the selected filters.</div>}
          {filtered.map((d) => (
            <div key={d.deadline_id} className="border-r border-b border-stone/40 bg-bone p-5 grid sm:grid-cols-12 gap-3 items-baseline" data-testid={`deadline-row-${d.deadline_id}`}>
              <div className="sm:col-span-2 text-[11px] uppercase tracking-[0.18em] text-brass font-semibold">{d.school}</div>
              <div className="sm:col-span-2 text-[11px] uppercase tracking-[0.18em] text-stone">{d.type} · {d.cycle_label}</div>
              <div className="sm:col-span-2 font-mono text-sm text-ink tabular-nums">{d.date}</div>
              <div className="sm:col-span-5 text-sm text-ink-soft leading-relaxed">{d.notes}{d.strategy_commentary && (<><div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-ivy">Strategy</div><div className="text-sm text-ink mt-1">{d.strategy_commentary}</div></>)}</div>
              <button type="button" onClick={() => downloadIcs(d)} className="sm:col-span-1 text-stone hover:text-ivy flex justify-end" title="Download .ics"><Download className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      ) : (
        <CalendarView deadlines={filtered} onDownload={downloadIcs} />
      )}
      <p className="text-[11px] text-stone italic mt-8">Informational only — verify each deadline against the university&rsquo;s current published cycle. Some rows are placeholders pending editorial review.</p>
    </ToolPage>
  );
}
function CalendarView({ deadlines, onDownload }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() };
  });
  const today = new Date();
  const firstDay = new Date(cursor.y, cursor.m, 1);
  const startDow = firstDay.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const prev = () => setCursor((c) => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 });
  const next = () => setCursor((c) => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 });
  const byDate = {};
  deadlines.forEach((d) => {
    const key = (d.date || "").slice(0, 10);
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(d);
  });
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push({ blank: true });
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, iso, items: byDate[iso] || [], isToday: iso === today.toISOString().slice(0, 10) });
  }
  while (cells.length % 7) cells.push({ blank: true });
  const monthName = firstDay.toLocaleString("en-US", { month: "long", year: "numeric" });
  const monthCount = deadlines.filter((d) => (d.date || "").startsWith(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`)).length;
  return (
    <div className="mt-6" data-testid="deadlines-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={prev} data-testid="cal-prev" className="text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ivy px-2 py-1">← Prev</button>
        <div className="font-serif text-xl text-ivy tracking-tight">{monthName}</div>
        <button onClick={next} data-testid="cal-next" className="text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ivy px-2 py-1">Next →</button>
      </div>
      <div className="grid grid-cols-7 border-t border-l border-stone/40" data-testid="deadlines-calendar-grid">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="border-r border-b border-stone/40 bg-bone-dim/40 px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brass font-semibold">{d}</div>
        ))}
        {cells.map((c, i) => (
          <div key={i} className={`border-r border-b border-stone/40 min-h-[92px] p-1.5 ${c.blank ? "bg-bone-dim/20" : "bg-bone"} ${c.isToday ? "border-l-2 border-l-ivy" : ""}`}>
            {!c.blank && (
              <>
                <div className="text-[10px] text-stone">{c.day}</div>
                <div className="mt-1 space-y-0.5">
                  {c.items.slice(0, 3).map((d) => (
                    <button key={d.deadline_id} onClick={() => onDownload(d)} title={`${d.school} · ${d.type}`} className="w-full text-left border border-brass/50 bg-brass/10 hover:bg-brass/20 px-1.5 py-0.5 text-[10px] text-ivy truncate block" data-testid={`cal-chip-${d.deadline_id}`}>
                      {d.school} · {d.type}
                    </button>
                  ))}
                  {c.items.length > 3 && <div className="text-[10px] text-stone px-1">+{c.items.length - 3} more</div>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      {monthCount === 0 && <div className="mt-3 border border-stone/30 bg-bone-dim/30 px-3 py-2.5 text-[11px] text-stone italic">Nothing scheduled this month. Add a milestone or transfer deadline to get started.</div>}
    </div>
  );
}
